import { z } from 'zod';

import {
  ApiOk,
  ClosedReason,
  Email,
  EntityId,
  I18nText,
  IsoDate,
  JoinCode,
  SessionRole,
  ToneName,
  Uid,
  Ulid,
  Via,
} from './primitives';
import { PlayerCommandNode } from './rtdb';

export const SessionStatus = z.enum(['active', 'closed', 'expired']);
export type SessionStatus = z.infer<typeof SessionStatus>;

/** A `sessions` row (D1 is the system of record; RTDB `meta` is the live mirror). */
export const SessionDTO = z.object({
  id: Ulid,
  joinCode: JoinCode,
  hostUid: Uid,
  hostName: z.string(),
  deviceId: z.string(),
  status: SessionStatus,
  startedAt: IsoDate,
  expiresAt: IsoDate,
  closedAt: IsoDate.nullable(),
  closedReason: ClosedReason.nullable(),
  /** Feature snapshot of the host (guests inherit). */
  features: z.array(z.string()),
});
export type SessionDTO = z.infer<typeof SessionDTO>;

/** A `session_participants` row. */
export const MemberDTO = z.object({
  uid: Uid,
  name: z.string(),
  photoUrl: z.string().nullable(),
  role: SessionRole,
  joinedAt: IsoDate,
  leftAt: IsoDate.nullable(),
});
export type MemberDTO = z.infer<typeof MemberDTO>;

export const SessionParams = z.object({ sid: Ulid });
export type SessionParams = z.infer<typeof SessionParams>;

export const QueueItemParams = z.object({ sid: Ulid, itemId: Ulid });
export type QueueItemParams = z.infer<typeof QueueItemParams>;

/** `GET /sessions/:sid` */
export const SessionResponse = z.object({
  session: SessionDTO,
  members: z.array(MemberDTO),
});
export type SessionResponse = z.infer<typeof SessionResponse>;

/**
 * `GET /sessions/precheck?code=` — the join-code twin of `GET /tv/pair/precheck` (docs/03 §2).
 *
 * `/guest` used to send a guest through the whole name/photo/email form before the first
 * request told them the code was wrong (QA 2026-09-15, UX-155). The screen now asks this the
 * moment it has six characters, exactly the way `/tv` gates its "Continuar".
 */
export const SessionPrecheckQuery = z.object({ code: JoinCode });
export type SessionPrecheckQuery = z.infer<typeof SessionPrecheckQuery>;
export type SessionPrecheckQueryInput = z.input<typeof SessionPrecheckQuery>;

export const SessionPrecheckResponse = z.object({
  valid: z.boolean(),
  /** The code names a session that has already ended (`valid` is then false). */
  expired: z.boolean(),
  /** Only when `valid`: enough to say "Sesión de Tomás · 3 personas" before joining. */
  session: z
    .object({ hostName: z.string(), memberCount: z.number().int().nonnegative() })
    .optional(),
});
export type SessionPrecheckResponse = z.infer<typeof SessionPrecheckResponse>;

/** `POST /sessions/join` */
export const SessionJoinBody = z.object({
  joinCode: JoinCode,
  name: z.string().trim().min(1).max(60),
  email: Email.optional(),
  photoPath: z.string().min(1).max(512).optional(),
});
export type SessionJoinBody = z.infer<typeof SessionJoinBody>;
export type SessionJoinBodyInput = z.input<typeof SessionJoinBody>;

export const SessionJoinResponse = z.object({
  session: SessionDTO,
  member: MemberDTO,
  /** Present when `email` was given: whether a (non-anonymous) account exists for it (docs/05 §3). */
  existingAccount: z.boolean().optional(),
});
export type SessionJoinResponse = z.infer<typeof SessionJoinResponse>;

/** `POST /sessions/:sid/leave` */
export const SessionLeaveResponse = ApiOk;
export type SessionLeaveResponse = z.infer<typeof SessionLeaveResponse>;

export const QueueItemStatus = z.enum(['queued', 'playing', 'played']);
export type QueueItemStatus = z.infer<typeof QueueItemStatus>;

export const AddedBy = z.object({
  uid: Uid,
  name: z.string(),
  photo: z.string().optional(),
});
export type AddedBy = z.infer<typeof AddedBy>;

/** Queue item as returned by the API; same shape as the RTDB node plus its id. */
export const QueueItemDTO = z.object({
  id: Ulid,
  songId: EntityId,
  title: z.string(),
  artistNames: z.string(),
  imageUrl: z.string().nullable(),
  tone: ToneName,
  toneLabel: I18nText,
  addedBy: AddedBy,
  addedVia: Via,
  order: z.number().int(),
  status: QueueItemStatus,
  addedAt: IsoDate,
  startedAt: IsoDate.optional(),
  playedAt: IsoDate.optional(),
});
export type QueueItemDTO = z.infer<typeof QueueItemDTO>;

/** `POST /sessions/:sid/queue` — the single enqueue path. */
export const EnqueueBody = z.object({
  songId: EntityId,
  tone: ToneName,
  via: Via,
});
export type EnqueueBody = z.infer<typeof EnqueueBody>;

export const EnqueueResponse = z.object({ item: QueueItemDTO });
export type EnqueueResponse = z.infer<typeof EnqueueResponse>;

/** `POST /sessions/:sid/queue/:itemId/play-now` */
export const PlayNowItemResponse = z.object({
  item: QueueItemDTO,
  command: PlayerCommandNode,
});
export type PlayNowItemResponse = z.infer<typeof PlayNowItemResponse>;

/** `POST /sessions/:sid/play-now` — enqueue + play-now atomically. */
export const PlayNowBody = z.object({
  songId: EntityId,
  tone: ToneName,
  /** Defaults to the caller kind (TV token → 'tv', otherwise 'mobile'). */
  via: Via.optional(),
});
export type PlayNowBody = z.infer<typeof PlayNowBody>;
export const PlayNowResponse = PlayNowItemResponse;
export type PlayNowResponse = z.infer<typeof PlayNowResponse>;

/**
 * `PATCH /sessions/:sid/queue/:itemId` — re-tunes a song that is still waiting, which is what
 * the phone's queue modal saves (docs/05 §4, QA iteration 3 M6). Only the tone is editable:
 * the position is `…/queue/reorder`'s job and everything else is the song itself.
 */
export const QueueItemPatchBody = z.object({ tone: ToneName });
export type QueueItemPatchBody = z.infer<typeof QueueItemPatchBody>;

export const QueueItemPatchResponse = z.object({ item: QueueItemDTO });
export type QueueItemPatchResponse = z.infer<typeof QueueItemPatchResponse>;

/** `DELETE /sessions/:sid/queue/:itemId` */
export const RemoveQueueItemResponse = ApiOk;
export type RemoveQueueItemResponse = z.infer<typeof RemoveQueueItemResponse>;

/** `PATCH /sessions/:sid/queue/reorder` */
export const ReorderBody = z.object({
  itemId: Ulid,
  /** `null` = move to the front. */
  afterItemId: Ulid.nullable(),
});
export type ReorderBody = z.infer<typeof ReorderBody>;

export const ReorderResponse = z.object({ item: QueueItemDTO });
export type ReorderResponse = z.infer<typeof ReorderResponse>;

/** `POST /sessions/:sid/player/command` */
export const PlayerCommandBody = z.object({
  type: z.enum(['play', 'pause', 'next', 'stop']),
  itemId: Ulid.optional(),
});
export type PlayerCommandBody = z.infer<typeof PlayerCommandBody>;

export const PlayerCommandResponse = z.object({ command: PlayerCommandNode });
export type PlayerCommandResponse = z.infer<typeof PlayerCommandResponse>;

/** `POST /sessions/:sid/playback` (TV only) */
export const PlaybackEvent = z.enum(['started', 'progress', 'ended', 'error']);
export type PlaybackEvent = z.infer<typeof PlaybackEvent>;

export const PlaybackBody = z.object({
  itemId: Ulid,
  event: PlaybackEvent,
  positionMs: z.number().int().nonnegative(),
  /** Adapter error code/message when `event='error'`. */
  error: z.string().max(500).optional(),
});
export type PlaybackBody = z.infer<typeof PlaybackBody>;

export const PlaybackResponse = z.object({
  ok: z.literal(true),
  /** True when this call marked the item as played (30 s rule). */
  played: z.boolean().optional(),
});
export type PlaybackResponse = z.infer<typeof PlaybackResponse>;

/** Position after which a song counts as played (docs/03 §2 "playback"). */
export const PLAYED_THRESHOLD_MS = 30_000;

/** `GET /sessions/:sid/qr` */
export const SessionQrResponse = z.object({
  joinUrl: z.string(),
  svg: z.string(),
});
export type SessionQrResponse = z.infer<typeof SessionQrResponse>;
